
import { ResourceWrapper } from "../ResourceWrapper.ts";
import Semantic from "../../../eventer/Semantic.ts";
import { LocalEventerPod } from "../local-eventer-pod.ts";
import { mergeMultiFeedEntries, MultiFeedEntry } from "./events-feed-utils.ts";
import { EventsFeed, getEventsFeed } from "./events-feed.ts";

export class MultiEventsFeed {

    readonly localEventerPod: LocalEventerPod

    private readonly onEntriesChanged: (entries: MultiFeedEntry[]) => void

    private readonly eventsFeeds = new Map<string, EventsFeed>()
    private entries: MultiFeedEntry[] = []

    constructor(
        localEventerPod: LocalEventerPod,
        sessionId: null | string,
        remotePods: string[],
        onEntriesChanged: (entries: MultiFeedEntry[]) => void,
    ) {
        this.localEventerPod = localEventerPod
        this.onEntriesChanged = onEntriesChanged
        remotePods.forEach((remotePod) => {
            const eventFeed = getEventsFeed(localEventerPod, sessionId ?? null, remotePod)
            this.eventsFeeds.set(remotePod, eventFeed)
            if (eventFeed.events !== undefined) {
                this.onEntriesLoaded(remotePod, eventFeed.events)
            }
        })
    }

    private feedEvents(): undefined | MultiFeedEntry[] {

        const maxStartDateMin = this.determineMaxStartDateMin()
        const minStartDateMax = this.determineMinStartDateMax()
        if (maxStartDateMin === undefined || minStartDateMax === undefined) {
            return undefined
        }

        return this.entries
            .filter((entry: MultiFeedEntry) => {
                const startDate = entry.event.getMinDate(Semantic.SchemaOrg.Predicates.startDate.uri)
                if (startDate === undefined) {
                    return true
                }
                if (maxStartDateMin !== null && startDate < maxStartDateMin) {
                    return false
                }
                if (minStartDateMax !== null && startDate > minStartDateMax) {
                    return false
                }
                return true
            })
    }

    private onEntriesLoaded = (remotePod: string, newEvents: ResourceWrapper[]) => {

        // even if there are no new events, we need to check for a new date range
        const feedEventsBefore = this.feedEvents()

        const newEntries: MultiFeedEntry[] = newEvents.map(event => ({ remotePod, event }))

        const mergedEntries: MultiFeedEntry[] = mergeMultiFeedEntries(this.entries, newEntries)
        if (mergedEntries.length !== this.entries.length || newEvents.length > 0) {
            // We've got new events to add or updated
            this.entries = mergedEntries
        }

        const feedEvents = this.feedEvents()
        if (feedEvents !== undefined && feedEventsBefore != feedEvents) {
            this.onEntriesChanged(feedEvents)
        }
    }

    private determineMaxStartDateMin = (): undefined | Date | null => {
        let maxStartDateMin: undefined | Date = undefined
        for (const eventFeed of this.eventsFeeds.values()) {
            if (eventFeed.startDateMin === undefined) {
                // If there is one undefined, the max is undefined (one of the feeds is not yet loaded)
                return undefined
            }
            if (eventFeed.startDateMin === null) {
                // this feed has no more earlier events, so we ignore it
                continue
            }
            if (maxStartDateMin === undefined) {
                maxStartDateMin = eventFeed.startDateMin
            } else if (maxStartDateMin < eventFeed.startDateMin) {
                maxStartDateMin = eventFeed.startDateMin
            }
        }
        return maxStartDateMin === undefined ? null : maxStartDateMin
    }

    private determineMinStartDateMax = (): undefined | Date | null => {
        let minStartDateMax: undefined | Date = undefined
        for (const eventFeed of this.eventsFeeds.values()) {
            if (eventFeed.startDateMax === undefined) {
                // If there is one undefined, the min is undefined
                return undefined
            }
            if (eventFeed.startDateMax === null) {
                // this feed has no more later events, so we ignore it
                continue
            }
            if (minStartDateMax === undefined) {
                minStartDateMax = eventFeed.startDateMax
            } else if (minStartDateMax > eventFeed.startDateMax) {
                minStartDateMax = eventFeed.startDateMax
            }
        }
        return minStartDateMax === undefined ? null : minStartDateMax
    }

    hasNext(): boolean {
        for (const eventFeed of this.eventsFeeds.values()) {
            if (eventFeed.hasNext()) {
                return true
            }
        }
        return false
    }

    async initNext(): Promise<void> {
        const loadPromises: Promise<any>[] = []
        for (const [remotePod, eventFeed] of this.eventsFeeds.entries()) {
            if (eventFeed.nextUrl === undefined) {
                const loadPromise = eventFeed
                    .loadNext()
                    .then((events) => {
                        this.onEntriesLoaded(remotePod, events)
                    })
                loadPromises.push(loadPromise)
            }
        }
        await Promise.all(loadPromises)
    }

    async loadNext(): Promise<void> {

        let minStartDateMax = this.determineMinStartDateMax()

        const loadPromises: Promise<any>[] = []
        for (const [remotePod, eventFeed] of this.eventsFeeds.entries()) {
            if (eventFeed.nextUrl === undefined || (minStartDateMax && eventFeed.startDateMax === minStartDateMax)) {
                const loadPromise = eventFeed
                    .loadNext()
                    .then((events) => {
                        this.onEntriesLoaded(remotePod, events)
                    })
                loadPromises.push(loadPromise)
            }
        }

        await Promise.all(loadPromises)
    }
}
